import "./App.css";

import { AuthProvider, DialogProvider } from "./context";
import {
  Availability,
  Home,
  Lessons,
  Login,
  Message,
  ProfileWrapper,
  Schedule,
  SetPassword,
} from "./pages";
import { Fragment, useEffect, useState } from "react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { ApiProvider } from "./Api/ApiProvider";
import AsTutor from "./pages/Registration/AsPartner";
import CheckMail from "./pages/check-mail";
import CreateTrialModal from "./pages/lessons/trial/TrialLessonModal";
import ErrorBoundary from "./components/ErrorBoundary";
import ForgotPassword from "./pages/forgot-password";
import { LanguageContextProvider } from "./context/LanguagesContext";
import PasswordReset from "./pages/password-reset";
import { PaymentProvider } from "./context/PaymentContext";
import { ProfileProvider } from "./context/ProfileContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RegisterForm from "./pages/Registration/Partner.reg";
import ScheduleDetail from "./pages/schedule/[lesson_id]";
import { Summary } from "./pages/home/withdraw-summary";
import { Toaster } from "sonner";
import { TrialLessonProvider } from "./context/TrailLessonContext";
import { Withdraw } from "./pages/home/withdraw";
import { socket } from "./socket";
import { useRole } from "./context/RoleContext";

function App() {
  const [isConnected, setIsConnected] = useState<any>(socket.connected);
  const [fooEvents, setFooEvents] = useState<any>([]);
  const [isTrialModalOpen, setTrialModalOpen] = useState(false);
  const { role } = useRole();

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onFooEvent(value: any) {
      setFooEvents((previous: any) => [...previous, value]);
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("foo", onFooEvent);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("foo", onFooEvent);
    };
  }, []);

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error: any) => {
        console.log(error);
      },
    }),
  });

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ApiProvider>
          <Toaster position="top-right" />
          <AuthProvider>
            <TrialLessonProvider>
              <LanguageContextProvider>
                <PaymentProvider>
                  <Router>
                    <DialogProvider>
                      <Routes>
                        <Route path="/" element={<Login />} />
                        <Route path="/login" element={<Login />} />

                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword />}
                        />

                        <Route path="/check-mail" element={<CheckMail />} />

                        <Route
                          path="/password-reset"
                          element={<PasswordReset />}
                        />
                        <Route
                          path="/register-as-partner"
                          element={<RegisterForm />}
                        />
                        <Route path="/set-password" element={<SetPassword />} />

                        <Route
                          path="/home"
                          element={
                            <ProtectedRoute>
                              <Home />
                            </ProtectedRoute>
                          }
                        />
                        {role?.toLowerCase() === "teacher" ? (
                          <Route
                            path="/lessons"
                            element={
                              <ProtectedRoute>
                                <Lessons />
                              </ProtectedRoute>
                            }
                          />
                        ) : (
                          <Route path="*" element={<ErrorBoundary />} />
                        )}
                        <Route
                          path="/schedule"
                          element={
                            <ProtectedRoute>
                              <Schedule />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/schedule/:lesson_id"
                          element={
                            <ProtectedRoute>
                              <ScheduleDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/availability"
                          element={
                            <ProtectedRoute>
                              <Availability />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/message"
                          element={
                            <ProtectedRoute>
                              <Message isConnected={isConnected} />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <ProfileWrapper />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/home/withdraw"
                          element={
                            <ProtectedRoute>
                              <Withdraw />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/home/withdraw/summary"
                          element={
                            <ProtectedRoute>
                              <Summary />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </DialogProvider>
                  </Router>

                  {isTrialModalOpen && (
                    <CreateTrialModal
                      handleCloseModal={() => setTrialModalOpen(false)}
                    />
                  )}
                </PaymentProvider>
              </LanguageContextProvider>
            </TrialLessonProvider>
          </AuthProvider>
        </ApiProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
